// Bundle stale pós-deploy: quando um import dinâmico falha (chunk com hash
// antigo removido do S3/CloudFront), o app está rodando um shell velho e há uma
// versão nova publicada. Este módulo NÃO recarrega a página: um reload
// automático apagaria dados de um formulário/cotação aberto em outro ponto da
// tela. Em vez disso, emite `alamo:chunk-stale` — o PwaUpdatePrompt escuta e
// mostra um toast de 1 clique, deixando o usuário salvar antes de recarregar.
//
// Um chunk pode falhar durante o BOOT, antes do PwaUpdatePrompt montar e
// registrar o listener. Para não perder esse prompt, o último evento fica
// pendente em memória (consumePendingChunkStale) e o componente drena no mount.

export const CHUNK_STALE_EVENT = 'alamo:chunk-stale';

declare global {
  interface WindowEventMap {
    // Emitido por chunkReload quando um import lazy falha por bundle stale.
    // Não carrega payload — o PwaUpdatePrompt só precisa saber que ocorreu.
    'alamo:chunk-stale': CustomEvent<undefined>;
  }
}

const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

// Colapsa uma rajada de erros (vários lazy imports falhando em sequência) num
// único evento. Rearmado sempre que o prompt é consumido, para não engolir um
// 2º deploy legítimo mais tarde na mesma sessão.
let armed = true;

// Guarda o evento até que haja um listener (cobre falha no boot).
let pending = false;

function isChunkLoadError(reason: unknown): boolean {
  if (reason instanceof Error) {
    return (
      reason.name === 'ChunkLoadError' ||
      CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(reason.message))
    );
  }
  if (typeof reason === 'string') {
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(reason));
  }
  return false;
}

function notifyChunkStale(): void {
  if (!armed) return;
  armed = false;
  pending = true;
  if (import.meta.env.DEV) {
    console.warn('[chunkReload] chunk stale detectado — prompt de recarga emitido');
  }
  window.dispatchEvent(new CustomEvent(CHUNK_STALE_EVENT));
}

// Consumido pelo PwaUpdatePrompt no mount: se um chunk falhou antes do listener
// existir, drena o evento perdido. Rearma o detector para permitir reemissão
// (2º deploy) — o toast em si é deduplicado por id no componente.
export function consumePendingChunkStale(): boolean {
  const wasPending = pending;
  pending = false;
  armed = true;
  return wasPending;
}

// Reseta o estado interno (arm/pending). Uso exclusivo de teste — o app importa
// este módulo uma única vez e nunca precisa reiniciá-lo.
export function __resetChunkReloadStateForTest(): void {
  armed = true;
  pending = false;
}

// Registra os listeners uma única vez por `window` (idempotente): o módulo é
// side-effect e não deve empilhar handlers se for reimportado.
const REGISTERED_FLAG = '__alamoChunkReloadRegistered__';
const win = window as typeof window & { [REGISTERED_FLAG]?: boolean };

if (!win[REGISTERED_FLAG]) {
  win[REGISTERED_FLAG] = true;

  // Vite emite `vite:preloadError` quando o preload de um chunk lazy falha.
  window.addEventListener('vite:preloadError', ((event: Event) => {
    event.preventDefault();
    notifyChunkStale();
  }) as EventListener);

  // Falhas de import() fora do preload do Vite chegam como promise rejeitada.
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    event.preventDefault();
    notifyChunkStale();
  });
}
